/* istanbul ignore file */
import dayjs from 'dayjs';

import { ContactSearchFilters } from './types/campaign';

import { dateToString, dayjsToString } from '@/utils/date';

export const getCampaignsReportUrl = (
  websiteId: string,
  from: Date,
  to: Date
): string =>
  `/website/${websiteId}/hsm/campaigns/report?from=${dateToString(
    from
  )}&to=${dayjsToString(dayjs(to).add(1, 'day'))}`;

export const getAccountLimitsUrl = (websiteId: string): string =>
  `/website/${websiteId}/hsm/account/limit`;

export const getHsmLineUrl = (websiteId: string): string =>
  `/website/${websiteId}/hsm/line`;

export const createCampaignUrl = '/hsm/campaign';

export const createCampaignUrlV4 = (websiteId: string): string =>
  `/website/${websiteId}/hsm/v4/campaigns`;

export const tierPrecheckUrl = (websiteId: string): string =>
  `/website/${websiteId}/hsm/v4/campaigns/tier-precheck`;

export const cancelCampaignUrl = (
  websiteId: string,
  campaignId: number
): string => `/website/${websiteId}/hsm/v3/campaigns/${campaignId}/cancel`;

export const uploadTemplateMediaUrl = (websiteId: string): string =>
  `/website/${websiteId}/hsm/v2/template-media`;

export const createTemplateV2Url = (websiteId: string): string =>
  `/website/${websiteId}/hsm/v2/templates`;

export const getSheetContactsUrl = (sheetid: string): string =>
  `/hsm/contacts/google-sheet/${sheetid}`;

export const getContactsFileExampleUrl = (
  websiteId: string,
  templateParamCount: number | null
): string => {
  let url = `/hsm/contacts/file-example?websiteId=${websiteId}`;

  if (templateParamCount != null) {
    url = `${url}&templateParamCount=${templateParamCount}`;
  }
  return url;
};

export const postFileContactsUrl = `/hsm/contacts/file`;

export const postWhatsappWebLoginUrl = (websiteId: string): string =>
  `/website/${websiteId}/hsm/web-login`;

export const postWhatsappWebLogoutUrl = (websiteId: string): string =>
  `/website/${websiteId}/hsm/web-logout`;

export const uploadCampaignMediaUrl = (websiteId: string): string =>
  `/website/${websiteId}/hsm/v4/campaign-media`;

export const getHsmWebsitesUrl = `/hsm/websites`;

export const getUsageMetricsUrl = (companyId: string) =>
  `/company/${companyId}/usage-metrics`;

export const updateTemplateUrl = (websiteId: string, templateName: string) =>
  `/website/${websiteId}/hsm/templates/${encodeURIComponent(templateName)}`;

export const getFullCampaignUrl = (websiteId: string, campaignId: string) =>
  `/website/${websiteId}/hsm/campaigns/${campaignId}/full`;

export const getNextRenewalDateUrl = (companyId: string) =>
  `/company/${companyId}/subscription-next-renewal-date`;

export const resendPendingMessagesUrl = (
  websiteId: string,
  campaignId: number
) => `/website/${websiteId}/hsm/campaigns/${campaignId}/resend`;

export const retryErroredMessagesUrl = (
  websiteId: string,
  campaignId: number
) => `/website/${websiteId}/hsm/campaigns/${campaignId}/retry-errored`;

export const getTemplatesUrl = (websiteId: string) =>
  `/website/${websiteId}/hsm/v2/templates`;

export const getNotificationEventsUrl = (
  websiteId: string,
  from?: string,
  to?: string
): string => {
  let url = `/website/${websiteId}/hsm/notification-events`;
  const params: string[] = [];

  if (from) {
    params.push(`from=${from}`);
  }
  if (to) {
    params.push(`to=${to}`);
  }
  if (params.length) {
    url += `?${params.join('&')}`;
  }
  return url;
};

export const getBlacklistApiUrl = (websiteId: string): string =>
  `/website/${websiteId}/hsm/blacklist`;

export interface ContactsSearchUrlParams {
  websiteId: string;
  page: number;
  limit: number;
  filters?: ContactSearchFilters;
}

export const getContactsSearchUrl = ({
  websiteId,
  page,
  limit,
  filters,
}: ContactsSearchUrlParams): string => {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });

  if (filters) {
    if (filters.search) {
      params.set('search', filters.search);
    }
    if (filters.tags) {
      params.set('tags', filters.tags);
    }
    if (filters.phaseId) {
      params.set('phaseId', filters.phaseId);
    }
    if (filters.assignedTo) {
      params.set('assignedTo', filters.assignedTo);
    }
    if (filters.lastInteractionFrom) {
      params.set('lastInteractionFrom', filters.lastInteractionFrom);
    }
    if (filters.lastInteractionTo) {
      params.set('lastInteractionTo', filters.lastInteractionTo);
    }
  }

  return `/website/${websiteId}/hsm/v4/campaigns/contacts/search?${params.toString()}`;
};

export const postContactsPreviewUrl = (websiteId: string): string =>
  `/website/${websiteId}/hsm/v4/campaigns/contacts/preview`;

export const patchContactsPreviewUrl = (
  websiteId: string,
  previewKey: string
): string =>
  `/website/${websiteId}/hsm/v4/campaigns/contacts/preview/${encodeURIComponent(
    previewKey
  )}`;

export const getContactAttributesUrl = (websiteId: string): string =>
  `/website/${websiteId}/hsm/v4/campaigns/contacts/attributes`;

export const getContactPhasesUrl = (websiteId: string): string =>
  `/website/${websiteId}/hsm/v4/campaigns/contacts/phases`;

export const getContactAgentsUrl = (websiteId: string): string =>
  `/website/${websiteId}/hsm/v4/campaigns/contacts/agents`;

export const getContactTagsUrl = (websiteId: string): string =>
  `/website/${websiteId}/hsm/v4/campaigns/contacts/tags`;

export const getSegmentsUrl = (websiteId: string): string =>
  `/website/${websiteId}/hsm/v4/segments`;

export const postSegmentPreviewUrl = (
  websiteId: string,
  segmentId: string
): string =>
  `/website/${websiteId}/hsm/v4/campaigns/segments/${encodeURIComponent(
    segmentId
  )}/preview`;

export const getUsageByUserReportUrl = (
  websiteId: string,
  from: Date,
  to: Date
): string =>
  // `to` is pushed one day forward for the same reason as the campaigns report:
  // the backend compares against a bare date, so the last day would be cut off.
  `/website/${websiteId}/hsm/reports/usage-by-user?from=${dateToString(
    from
  )}&to=${dayjsToString(dayjs(to).add(1, 'day'))}`;
